import * as React from "react";
import { Button, Image, MessageBar, MessageBarBody, Text, makeStyles, tokens } from "@fluentui/react-components";

import { CredentialForm } from "./CredentialForm";
import { DialogCancelled, canDisplayDialog, openSignInDialog } from "../lib/officeDialog";
import type { Session } from "../lib/session";
// Handled by webpack's asset/resource rule, so this resolves to a URL.
import wordmark from "../../assets/valoon-wordmark.png";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: "20px", alignItems: "stretch" },
  brand: { display: "flex", justifyContent: "center", paddingTop: "8px" },
  lede: { color: tokens.colorNeutralForeground2, textAlign: "center" },
});

export interface SignInGateProps {
  onSignedIn: (session: Session) => void;
}

/**
 * What the pane shows with no session: a single "Connect to Valoon" button
 * that opens the Office dialog.
 *
 * If the host cannot open a dialog — Outlook mobile and classic web against
 * on-premises Exchange have no Dialog API — the same credential form is
 * rendered inline instead. That is why the manifest requires only Mailbox 1.5
 * and not the DialogApi requirement set: falling back costs one component,
 * excluding those clients would cost the customers on them.
 */
export const SignInGate: React.FC<SignInGateProps> = ({ onSignedIn }) => {
  const styles = useStyles();
  const [inline, setInline] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);

  const connect = async () => {
    setNotice(null);
    if (!canDisplayDialog()) {
      setInline(true);
      return;
    }
    setBusy(true);
    try {
      onSignedIn(await openSignInDialog());
    } catch (err) {
      if (err instanceof DialogCancelled) {
        setNotice(null);
      } else {
        // Anything else means the dialog is not usable here; the inline form
        // is not a degraded experience, just a differently placed one.
        setInline(true);
      }
      setBusy(false);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.brand}>
        <Image src={wordmark} alt="Valoon" height={36} fit="contain" />
      </div>

      {notice && (
        <MessageBar intent="warning">
          <MessageBarBody>{notice}</MessageBarBody>
        </MessageBar>
      )}

      {inline ? (
        <CredentialForm onSignedIn={onSignedIn} />
      ) : (
        <>
          <Text className={styles.lede}>Connect your Valoon account to create tickets from your email.</Text>
          <Button appearance="primary" onClick={connect} disabled={busy}>
            {busy ? "Waiting for sign-in…" : "Connect to Valoon"}
          </Button>
        </>
      )}
    </div>
  );
};
